import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Joyride, STATUS, EVENTS } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { consentHeaders, hasConsent } from '@/lib/consent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fireTourEvent = (tourId, event) => {
  // Best-effort fire-and-forget — never blocks UX, never raises.
  try {
    if (!hasConsent('analytics')) return; // RGPD: só com consentimento de análise
    const url = `${BACKEND_URL}/api/track/event?c=${encodeURIComponent(`tour_${event}_${tourId}`)}`;
    axios.post(url, null, { timeout: 3000, headers: consentHeaders() }).catch(() => {});
  } catch { /* noop */ }
};

/**
 * Reusable onboarding tour. Auto-starts the first time a given `tourId` is seen
 * on this device (tracked via localStorage). Users can dismiss/finish to opt out
 * permanently. A small "Re-ver tour" button is exposed via the `onReplay` prop
 * pattern (host page can call resetTour from a ref).
 *
 * Usage:
 *   <Tour tourId="branding-v1" steps={[{target: '[data-testid="..."]', content: '...'}, ...]} />
 */
export default function Tour({ tourId, steps, autoStart = true }) {
  const { t } = useTranslation();
  const storageKey = `tour_done_${tourId}`;
  const [run, setRun] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!autoStart) { setReady(true); return; }
    const done = typeof window !== 'undefined' && window.localStorage.getItem(storageKey) === '1';
    // Slight delay so the target elements have time to mount
    const t = setTimeout(() => {
      const willRun = !done;
      setRun(willRun);
      setReady(true);
      if (willRun) fireTourEvent(tourId, 'started');
    }, 600);
    return () => clearTimeout(t);
  }, [storageKey, autoStart, tourId]);

  const handleCallback = useCallback((data) => {
    if (data.type === EVENTS.STEP_AFTER) {
      // Per-step completion — useful to identify drop-off step
      fireTourEvent(tourId, `step_${data.index}`);
    }
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(data.status)) {
      window.localStorage.setItem(storageKey, '1');
      setRun(false);
      fireTourEvent(tourId, data.status === STATUS.FINISHED ? 'completed' : 'skipped');
    }
  }, [storageKey, tourId]);

  if (!ready || !Array.isArray(steps) || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollOffset={80}
      disableOverlayClose
      callback={handleCallback}
      locale={{
        back: t('tour.back'), close: t('common.close'), last: t('tour.last'),
        next: t('tour.next'), open: t('common.open'), skip: t('tour.skip'),
      }}
      styles={{
        options: {
          arrowColor: '#121212',
          backgroundColor: '#121212',
          overlayColor: 'rgba(0, 0, 0, 0.7)',
          primaryColor: '#A855F7',
          textColor: '#fff',
          zIndex: 9999,
        },
        tooltip: { borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)' },
        tooltipContainer: { textAlign: 'left' },
        tooltipTitle: { fontFamily: 'Outfit, sans-serif', fontSize: 16, fontWeight: 700 },
        tooltipContent: { fontSize: 14, lineHeight: 1.6 },
        buttonNext: { backgroundColor: '#A855F7', borderRadius: 8, fontWeight: 600 },
        buttonBack: { color: 'rgba(255,255,255,0.6)' },
        buttonSkip: { color: 'rgba(255,255,255,0.4)', fontSize: 12 },
      }}
    />
  );
}

/** Helper: clear a tour's completion flag so it runs again. Useful for "Ver tour novamente" CTAs. */
export const resetTour = (tourId) => {
  try { window.localStorage.removeItem(`tour_done_${tourId}`); } catch { /* noop */ }
};
